import { useEffect, useMemo, useRef } from 'react';
import { useTeamPresenceStore, selectTaskViewers, selectBlockPresence } from '../store/teamPresenceStore';
import {
  pushTeamPresenceContext,
  pushCollabOps,
  addTeamEventListener,
  COLLAB_BLOCK_OPS_EVENT,
} from '../services/teamChannelService';
import { stableStringify } from '../components/ui/lexicalBlocks';

/*
 * A team task's live side, moved out of TaskDetailModal unchanged when the
 * modal was redesigned and split (owner's mock, 05.10.2026): task-level
 * presence ("who else has this task open") and the per-line live collab of
 * the description — echo safety, own-save ring, recency-gated reconcile.
 * A private-board task (`isTeamBoard` false) runs none of it.
 */

// A record reconcile (task.descriptionLexical landing via the parent's own
// save round-trip or a teammate's) may only apply while this client has been
// quiet for at least this long — no local edit, no inbound live op applied.
// During active editing/watching an incoming record is guaranteed stale (see
// attemptReconcile below); applying it produces exactly the rubber-banding
// this whole mechanism exists to avoid.
const RECONCILE_RECENCY_GATE_MS = 1500;
// Retry delay for a reconcile stashed because the client was still active.
const RECONCILE_RETRY_MS = 1600;
// How many of this client's own recent outbound description patches (Lexical
// blocks, stableStringify'd — see lexicalBlocks.js) are kept to recognize an
// inbound record as our own echo.
const OWN_SAVE_ECHO_RING_SIZE = 4;

/**
 * @returns {{ taskViewers, descEditorRef, initialDescBlocks, collabConfig,
 *   handleTeamDescriptionHtmlChange, handleTeamDescriptionBlocksChange }}
 */
export default function useTaskDescriptionCollab({ task, teamId, isTeamBoard, isActive, onChange, user, currentUserId }) {
  // ---- Task-level presence: "I'm viewing this task" for as long as the modal
  // is open and this tab is the active one. Cleared (both task_id and block_id,
  // so a mid-edit block highlight doesn't linger) whenever either flips off.
  const teamPresenceByTeam = useTeamPresenceStore((s) => s.byTeam);
  const taskViewers = useMemo(
    () => (isTeamBoard ? selectTaskViewers(teamPresenceByTeam, teamId, task.id, currentUserId) : []),
    [isTeamBoard, teamPresenceByTeam, teamId, task.id, currentUserId]
  );
  const blockPresence = useMemo(
    () => (isTeamBoard ? selectBlockPresence(teamPresenceByTeam, teamId, { taskId: task.id }, currentUserId) : []),
    [isTeamBoard, teamPresenceByTeam, teamId, task.id, currentUserId]
  );
  useEffect(() => {
    if (!isActive || !isTeamBoard) return undefined;
    pushTeamPresenceContext(teamId, { task_id: task.id });
    return () => pushTeamPresenceContext(teamId, { task_id: null, block_id: null, block_offset: null });
  }, [isActive, isTeamBoard, teamId, task.id]);

  // ---- Description per-line collab (team boards only — see the editor render
  // below; a private board keeps the plain HTML value/onChange path and none
  // of this runs).
  //
  // Echo safety for INBOUND live ops: applyingRemoteRef is true for exactly the
  // window between calling ref.applyRemoteOps/applyFullBlocks and that call's
  // own onRemoteApplied firing, so the onChange/onBlocksChange callbacks below
  // can tell "this settled-change is us mirroring a remote edit" apart from a
  // real local keystroke and skip re-emitting/re-saving it. LexicalRichEditor
  // commits updates on a microtask (not synchronously), and a call whose ops
  // all land on the block the user has focused can resolve to a no-op with no
  // onRemoteApplied at all — remoteApplyTokenRef guards against the flag being
  // reset by an unrelated earlier call's fallback in that situation (see
  // beginRemoteApply/endRemoteApply).
  const descEditorRef = useRef(null);
  const applyingRemoteRef = useRef(false);
  const remoteApplyTokenRef = useRef(0);
  const beginRemoteApply = () => {
    remoteApplyTokenRef.current += 1;
    applyingRemoteRef.current = true;
    return remoteApplyTokenRef.current;
  };
  const endRemoteApply = (token) => {
    if (remoteApplyTokenRef.current === token) applyingRemoteRef.current = false;
  };

  // LexicalRichEditor's `valueBlocks` is read once on mount, not reactive —
  // freezing it here (keyed only by task.id, so it doesn't change for the
  // life of this modal instance) matches that contract. A task that already
  // has descriptionLexical opens straight into block-collab mode; a legacy
  // (HTML-only) task stays on the existing reactive `value`/HtmlSyncPlugin
  // path for this whole session — even after a local edit dual-writes
  // descriptionLexical for the first time — so a later remote update can
  // never race the two content-import paths against each other. The first
  // local edit still saves descriptionLexical (dual-write), so the NEXT time
  // this task is opened it starts in full block-collab mode.
  const initialDescBlocks = useMemo(
    () => (task.descriptionLexical && task.descriptionLexical.blocks ? task.descriptionLexical : null),
    [task.id] // eslint-disable-line react-hooks/exhaustive-deps
  );
  // JSON of the descriptionLexical this modal instance already reflects —
  // either because it just produced it locally (set right before the outbound
  // patch fires) or because it already reconciled a remote one. Guards the
  // outbound direction: our own patch round-tripping back through `task` must
  // not bounce straight back into the editor as a "remote" reconcile.
  const lastKnownDescLexicalJsonRef = useRef(initialDescBlocks ? stableStringify(initialDescBlocks) : null);

  // Ring buffer of stableStringify(descriptionLexical) for the last few
  // outbound patches THIS client has issued (pushed the moment each patch is
  // built — see flushPendingDescPatch). An inbound task.descriptionLexical
  // matching one of these is unambiguously our own save echoing back through
  // the parent's store round-trip, not a teammate's edit.
  const savedDescLexicalRingRef = useRef([]);
  const recordOwnDescSave = (snapshot) => {
    if (!snapshot) return;
    const ring = savedDescLexicalRingRef.current;
    ring.push(stableStringify(snapshot));
    if (ring.length > OWN_SAVE_ECHO_RING_SIZE) ring.shift();
  };
  // Bumped on every local editor change that schedules an outbound patch, and
  // whenever an inbound collab:block_ops batch is applied — read by
  // attemptReconcile's recency gate (see RECONCILE_RECENCY_GATE_MS above).
  const lastLocalEditAtRef = useRef(0);
  const lastOpsAppliedAtRef = useRef(0);
  // Latest descriptionLexical deferred by the recency gate (superseded by any
  // newer arrival) and the single retry timer used to re-attempt it.
  const pendingReconcileLexRef = useRef(null);
  const reconcileRetryTimerRef = useRef(null);
  const attemptReconcileRef = useRef(null);

  const scheduleReconcileRetry = () => {
    if (reconcileRetryTimerRef.current) return;
    reconcileRetryTimerRef.current = setTimeout(() => {
      reconcileRetryTimerRef.current = null;
      const pending = pendingReconcileLexRef.current;
      pendingReconcileLexRef.current = null;
      if (pending) attemptReconcileRef.current?.(pending);
    }, RECONCILE_RETRY_MS);
  };

  /** Attempts to reconcile one inbound task.descriptionLexical into the live
   *  editor. Cheap short-circuit first (the existing equality guard — this
   *  exact snapshot was already applied or is our own most-recent echo), then
   *  the own-echo ring buffer (an OLDER save of ours landing after a newer
   *  local edit — equality alone can't catch this, see T1/T2), then a
   *  recency gate: only apply while both local edits and inbound live ops
   *  have been quiet for RECONCILE_RECENCY_GATE_MS, otherwise stash (replacing
   *  any earlier stash) and retry once activity should have settled. */
  const attemptReconcile = (lex) => {
    // Any record reaching a decision below supersedes whatever's currently
    // stashed — records are processed in arrival order, so if we're now
    // looking at lex, an earlier stash is definitionally stale relative to
    // it. Only the busy branch below re-arms the stash, with lex itself.
    // Without this, a stash surviving an unrelated later record's immediate
    // apply (or equality/own-echo skip) would fire via its own retry timer
    // and revert a task description that has already moved on.
    pendingReconcileLexRef.current = null;
    const json = stableStringify(lex);
    if (json === lastKnownDescLexicalJsonRef.current) return; // already applied / our own most-recent echo
    if (savedDescLexicalRingRef.current.includes(json)) {
      // An older save of ours — converge the baseline so we stop re-checking
      // it, but there's nothing to apply to the editor (it already has
      // newer content than this record reflects).
      lastKnownDescLexicalJsonRef.current = json;
      return;
    }
    const now = Date.now();
    const busy =
      now - lastLocalEditAtRef.current < RECONCILE_RECENCY_GATE_MS ||
      now - lastOpsAppliedAtRef.current < RECONCILE_RECENCY_GATE_MS;
    if (busy) {
      pendingReconcileLexRef.current = lex;
      scheduleReconcileRetry();
      return;
    }
    lastKnownDescLexicalJsonRef.current = json;
    // beginRemoteApply/endRemoteApply mirror the ops-listener below: without
    // this, applyingRemoteRef would stay false while the editor's settled-
    // change callbacks fire for this apply, and handleTeamDescription*Change
    // would misread it as a real local edit — generating a redundant outbound
    // patch and (worse, given lastLocalEditAtRef below) making the recency
    // gate treat our own reconcile as fresh local activity.
    const token = beginRemoteApply();
    descEditorRef.current?.applyFullBlocks(lex);
    Promise.resolve().then(() => endRemoteApply(token));
  };
  attemptReconcileRef.current = attemptReconcile;

  // Clear any pending reconcile retry when the task changes or the modal
  // unmounts — a stash targets one specific task's content.
  useEffect(() => () => {
    if (reconcileRetryTimerRef.current) {
      clearTimeout(reconcileRetryTimerRef.current);
      reconcileRetryTimerRef.current = null;
    }
    pendingReconcileLexRef.current = null;
  }, [task.id]);

  // Inbound ephemeral ops from a teammate typing in the same task right now.
  useEffect(() => {
    if (!isTeamBoard) return undefined;
    return addTeamEventListener(COLLAB_BLOCK_OPS_EVENT, (payload, meta) => {
      if (String(meta?.teamId) !== String(teamId)) return;
      if (!payload || payload.entity_type !== 'task' || String(payload.entity_id) !== String(task.id)) return;
      if (!Array.isArray(payload.ops) || !payload.ops.length) return;
      lastOpsAppliedAtRef.current = Date.now();
      const token = beginRemoteApply();
      descEditorRef.current?.applyRemoteOps(payload.ops, { actorUserId: payload.actor?.user_id });
      // Fallback clear for the case applyRemoteOps resolves to a no-op (every
      // op targeted the block the local caret is in) — no onRemoteApplied
      // fires then, so nothing else would ever reset the flag.
      Promise.resolve().then(() => endRemoteApply(token));
    });
  }, [isTeamBoard, teamId, task.id]);

  // Authoritative reconcile: a newer persisted task record (via the
  // entity-dirty-aware WS fast path or a disk re-read) landing on the open
  // modal. Only for instances that opened already in block-collab mode (see
  // initialDescBlocks above) — a legacy-opened instance relies on the
  // existing unfocused HtmlSync path via the `value` prop instead. See
  // attemptReconcile above for the own-echo/recency-gated apply logic — a
  // reconcile mid-typing (T1) or mid-watching-a-live-editor (T2) is
  // guaranteed stale, and applying it is exactly what caused the
  // truncation/rubber-banding this whole mechanism exists to avoid.
  useEffect(() => {
    if (!isTeamBoard || !initialDescBlocks) return undefined;
    const lex = task.descriptionLexical;
    if (!lex || !lex.blocks) return undefined;
    attemptReconcile(lex);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTeamBoard, initialDescBlocks, task.descriptionLexical]);

  // Outbound: onBlocksChange and onChange(html) both fire for every settled
  // content change (call order differs between a real local edit and a
  // remote-apply echo — see LexicalRichEditor — so neither can assume the
  // other already ran); each stashes its half and the second one to arrive
  // flushes a single combined patch through the normal handleChangeTask path
  // (one dirty entry, matching the legacy single-onChange(html) shape below).
  const pendingDescPatchRef = useRef({ html: undefined, blocks: undefined });
  const flushPendingDescPatch = () => {
    const p = pendingDescPatchRef.current;
    if (p.html === undefined || p.blocks === undefined) return;
    const patch = { descriptionHtml: p.html, descriptionLexical: p.blocks };
    pendingDescPatchRef.current = { html: undefined, blocks: undefined };
    lastKnownDescLexicalJsonRef.current = stableStringify(patch.descriptionLexical);
    recordOwnDescSave(patch.descriptionLexical);
    onChange(patch);
  };
  const handleTeamDescriptionHtmlChange = (html) => {
    if (applyingRemoteRef.current) return; // remote echo — editor already shows it, don't re-save/re-broadcast
    lastLocalEditAtRef.current = Date.now();
    pendingDescPatchRef.current.html = html;
    flushPendingDescPatch();
  };
  const handleTeamDescriptionBlocksChange = (snapshot) => {
    if (applyingRemoteRef.current) return;
    lastLocalEditAtRef.current = Date.now();
    pendingDescPatchRef.current.blocks = snapshot;
    flushPendingDescPatch();
  };
  const collabConfig = useMemo(() => {
    if (!isTeamBoard) return null;
    return {
      onOps: (ops) => pushCollabOps(teamId, { entity_type: 'task', entity_id: task.id, ops }),
      // Orders the two texts when two people's edits to one block collide (lexicalMerge.js).
      selfUserId: currentUserId,
      onActiveBlock: (blockId, offset) => pushTeamPresenceContext(teamId, {
        task_id: task.id,
        block_id: blockId,
        block_offset: blockId == null ? null : offset,
      }),
      onRemoteApplied: (snapshot) => {
        // Also update the equality-guard baseline from the ACTUAL resulting
        // snapshot (not just the incoming record) — covers both inbound live
        // ops (which never touched lastKnownDescLexicalJsonRef before) and a
        // caret-protected partial apply, whose true resulting content can
        // differ from the record that triggered it.
        if (snapshot && snapshot.blocks) lastKnownDescLexicalJsonRef.current = stableStringify(snapshot);
        endRemoteApply(remoteApplyTokenRef.current);
      },
      remotePresence: blockPresence,
      // Typist sees their own marker exactly as teammates do — but only when
      // someone else has this task open.
      selfMarker:
        taskViewers.length > 0 && user?.id != null
          ? { userId: user.id, name: user.name || user.email || 'You' }
          : null,
    };
  }, [isTeamBoard, teamId, task.id, blockPresence, taskViewers, user, currentUserId]);

  return {
    taskViewers,
    descEditorRef,
    initialDescBlocks,
    collabConfig,
    handleTeamDescriptionHtmlChange,
    handleTeamDescriptionBlocksChange,
  };
}
